import React from 'react';
import { FormGroup, FormControlLabel, Checkbox, Box, styled, TextField } from '@mui/material';
import { Question } from '../../types';

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.5)',
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiTypography-root': {
    color: 'text.primary',
    fontSize: '1rem',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(4),
  width: 'calc(100% - 32px)',
  '& .MuiInputBase-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    borderColor: theme.palette.primary.main,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
}));

interface Props {
  question: Question;
  selectedOptions: number[];
  onChange: (optionIds: number[]) => void;
  customOtherValue?: string;
  onCustomOtherChange?: (value: string) => void;
}

export const MultipleSelectQuestion: React.FC<Props> = ({ 
  question, 
  selectedOptions, 
  onChange,
  customOtherValue = '',
  onCustomOtherChange = () => {}
}) => {
  const handleChange = (optionId: number) => {
    const newSelection = selectedOptions.includes(optionId)
      ? selectedOptions.filter(id => id !== optionId)
      : [...selectedOptions, optionId];
    onChange(newSelection);
  };

  // Find the 'Other' option if it exists
  const otherOption = question.options?.find(option => option.is_other);
  
  // Check if the 'Other' option is selected
  const isOtherSelected = otherOption && selectedOptions.includes(otherOption.id);

  return (
    <Box sx={{ width: '100%' }}>
      <FormGroup>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(question.options ?? []).map((option) => (
            <React.Fragment key={option.id}>
              <StyledFormControlLabel
                control={
                  <StyledCheckbox
                    checked={selectedOptions.includes(option.id)}
                    onChange={() => handleChange(option.id)}
                  />
                }
                label={option.front_text}
              />
              {option.is_other && selectedOptions.includes(option.id) && (
                <StyledTextField
                  label="Please specify"
                  variant="outlined"
                  size="small"
                  value={customOtherValue}
                  onChange={(e) => onCustomOtherChange(e.target.value)}
                  fullWidth
                  placeholder="Enter your custom response"
                  required
                  autoFocus
                  error={customOtherValue.trim() === ''}
                  helperText={customOtherValue.trim() === '' ? '' : ''}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </FormGroup>
    </Box>
  );
};